/**
 * Timeline Tab Component for V4 Dashboard
 * Uses ModularTimeline component following admin timeline standards
 */

import { useState } from 'react'
import { Box, Alert, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { sortWith, descend } from 'ramda'
import ModularTimeline, {
  TimelineGroup,
} from 'src/v3/components/timeline/ModularTimeline'
import { useTimelineData } from '../../hooks/use-timeline-data'
import { useDashboardData } from '../../hooks/use-dashboard-data'
import {
  isCEActivity,
  isAssignmentEvent,
  isUserAction,
  isLicenseActivity,
  isCredentialActivity,
  type AssignmentEventData,
  type CEActivityData,
  type UserActionData,
  type LicenseActivityData,
  type CredentialActivityData,
} from '../../types'
import { transformToModularTimelineEvent } from '../../utils/timeline-transforms'
import AssignmentActivityModal from '../modals/assignment-activity-modal'
import CertificateActivityModal from '../modals/certificate-activity-modal'
import UserActionModal from '../modals/user-action-modal'
import LicenseActivityModal from '../modals/license-activity-modal'
import CredentialActivityModal from '../modals/credential-activity-modal'

interface TimelineTabProps {
  filterState: ReturnType<typeof import('../../hooks/use-filters').useFilters>
}

export default function TimelineTab({ filterState }: TimelineTabProps) {
  let { filters, queryParams, updateFilter } = filterState

  // Modal state
  let [selectedAssignmentEvent, setSelectedAssignmentEvent] =
    useState<AssignmentEventData | null>(null)
  let [selectedCEActivity, setSelectedCEActivity] =
    useState<CEActivityData | null>(null)
  let [selectedCEActivityType, setSelectedCEActivityType] = useState<
    string | null
  >(null)
  let [selectedUserAction, setSelectedUserAction] =
    useState<UserActionData | null>(null)
  let [selectedLicenseActivity, setSelectedLicenseActivity] =
    useState<LicenseActivityData | null>(null)
  let [selectedCredentialActivity, setSelectedCredentialActivity] =
    useState<CredentialActivityData | null>(null)
  let [assignmentModalOpen, setAssignmentModalOpen] = useState(false)
  let [certificateModalOpen, setCertificateModalOpen] = useState(false)
  let [userActionModalOpen, setUserActionModalOpen] = useState(false)
  let [licenseModalOpen, setLicenseModalOpen] = useState(false)
  let [credentialModalOpen, setCredentialModalOpen] = useState(false)

  // Ensure required data is loaded and linked service is auto-selected
  let { hasRequiredData, groups } = useDashboardData(
    queryParams.linkedServiceID,
    (serviceID: string) => updateFilter('linkedServiceID', serviceID),
  )

  // Fetch timeline data (only API-level filters: linkedService + dates)
  let {
    data: timelineResponse,
    isLoading: isTimelineLoading,
    error,
    isError,
  } = useTimelineData(
    queryParams.linkedServiceID,
    queryParams.startDate,
    queryParams.endDate,
    hasRequiredData,
  )

  // Combined loading state
  let isLoading = !hasRequiredData || isTimelineLoading

  // Transform API events to ModularTimeline format and apply FE filters
  let timelineGroups: TimelineGroup[] = timelineResponse?.events
    ? (() => {
        // Apply frontend filters
        let filteredEvents = timelineResponse.events.filter((event) => {
          // Filter out license and credential events when toggle is off
          if (!filters.showLicenseCredentialEvents) {
            if (
              isLicenseActivity(event.data) ||
              isCredentialActivity(event.data)
            ) {
              return false
            }
          }

          // Filter by users
          if (queryParams.userIDs?.length) {
            if (isCEActivity(event.data)) {
              if (!queryParams.userIDs.includes(event.data.ceActivity.userID))
                return false
            } else if (isAssignmentEvent(event.data)) {
              // For assignments, we could check if any assigned users match, but assignments don't have a single user
              // For now, let assignments through if user filter is set
            } else if (isUserAction(event.data)) {
              if (!queryParams.userIDs.includes(event.data.userAction.userID))
                return false
            } else if (isLicenseActivity(event.data)) {
              if (
                !queryParams.userIDs.includes(event.data.licenseActivity.userID)
              )
                return false
            } else if (isCredentialActivity(event.data)) {
              if (
                !queryParams.userIDs.includes(
                  event.data.credentialActivity.userID,
                )
              )
                return false
            }
          }

          // Filter by categories
          if (queryParams.categories?.length) {
            if (isCEActivity(event.data)) {
              if (
                !queryParams.categories.includes(event.data.ceActivity.category)
              )
                return false
            }
            // Assignments don't have categories, so they pass through
          }

          // Filter by license types
          if (queryParams.licenseTypes?.length) {
            if (isCEActivity(event.data)) {
              if (
                !event.data.ceActivity.licenseTypes.some((lt) =>
                  queryParams.licenseTypes!.includes(lt),
                )
              )
                return false
            }
            // Assignments don't have license types, so they pass through
          }

          // Filter by groups (expand to user IDs first)
          if (queryParams.groupIDs?.length) {
            // Get all user IDs from selected groups
            let groupUserIDs = new Set<string>()
            queryParams.groupIDs.forEach((groupId) => {
              let selectedGroup = groups.find((g) => g.id === groupId)
              if (selectedGroup?.users) {
                selectedGroup.users.forEach((userId) =>
                  groupUserIDs.add(userId),
                )
              }
            })

            if (isCEActivity(event.data)) {
              if (!groupUserIDs.has(event.data.ceActivity.userID)) return false
            } else if (isUserAction(event.data)) {
              if (!groupUserIDs.has(event.data.userAction.userID)) return false
            } else if (isLicenseActivity(event.data)) {
              if (!groupUserIDs.has(event.data.licenseActivity.userID))
                return false
            } else if (isCredentialActivity(event.data)) {
              if (!groupUserIDs.has(event.data.credentialActivity.userID))
                return false
            }
            // For assignments, let them pass through for now
          }

          // Filter by activity types
          if (queryParams.activityTypes?.length) {
            if (isCEActivity(event.data)) {
              if (!queryParams.activityTypes.includes('ce-activity')) {
                return false
              }
            } else if (isAssignmentEvent(event.data)) {
              if (!queryParams.activityTypes.includes('assignment')) {
                return false
              }
            } else if (isUserAction(event.data)) {
              if (!queryParams.activityTypes.includes('user-action')) {
                return false
              }
            } else if (isLicenseActivity(event.data)) {
              if (!queryParams.activityTypes.includes('license-activity')) {
                return false
              }
            } else if (isCredentialActivity(event.data)) {
              if (!queryParams.activityTypes.includes('credential-activity')) {
                return false
              }
            } else {
              return false // Unknown event type
            }
          }

          return true
        })

        let transformedEvents = filteredEvents.map(
          transformToModularTimelineEvent,
        )

        // Group events by date
        let groupedByDate = transformedEvents.reduce(
          (eventGroups, event) => {
            let date = event.timestamp
              ? dayjs(event.timestamp).format('YYYY-MM-DD')
              : 'unknown'
            let existingEvents = eventGroups[date] || []
            return {
              ...eventGroups,
              [date]: [...existingEvents, event],
            }
          },
          {} as Record<string, typeof transformedEvents>,
        )

        // Convert to TimelineGroup array, sorted by date (newest first)
        let timelineGroupsArray = Object.entries(groupedByDate).map(
          ([date, events]) => ({
            date,
            events: sortWith(
              [
                descend((event: any) => {
                  if (!event.timestamp) return 0
                  return dayjs(event.timestamp).valueOf()
                }),
              ],
              events,
            ),
            totalEvents: events.length,
          }),
        )

        return sortWith(
          [descend((group: any) => dayjs(group.date).valueOf())],
          timelineGroupsArray,
        )
      })()
    : []

  // Handle event clicks
  let handleEventClick = (eventData: any) => {
    let originalEvent = eventData.data

    if (isCEActivity(originalEvent.data)) {
      let ceData = originalEvent.data.ceActivity
      setSelectedCEActivity(ceData)
      setSelectedCEActivityType(originalEvent.type)
      setCertificateModalOpen(true)
    } else if (isAssignmentEvent(originalEvent.data)) {
      let assignmentData = originalEvent.data.assignmentEvent
      setSelectedAssignmentEvent(assignmentData)
      setAssignmentModalOpen(true)
    } else if (isUserAction(originalEvent.data)) {
      let userActionData = originalEvent.data.userAction
      setSelectedUserAction(userActionData)
      setUserActionModalOpen(true)
    } else if (isLicenseActivity(originalEvent.data)) {
      let licenseData = originalEvent.data.licenseActivity
      setSelectedLicenseActivity(licenseData)
      setLicenseModalOpen(true)
    } else if (isCredentialActivity(originalEvent.data)) {
      let credentialData = originalEvent.data.credentialActivity
      setSelectedCredentialActivity(credentialData)
      setCredentialModalOpen(true)
    }
  }

  // Show error state
  if (isError) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error">
          Failed to load timeline data: {error?.message}
        </Alert>
      </Box>
    )
  }

  // Show empty state when no data and not loading
  if (!isLoading && timelineGroups.length === 0) {
    return (
      <Box sx={{ mt: 3, textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No activities found for the selected filters
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Try adjusting your date range or removing some filters
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 3 }}>
      <ModularTimeline
        groups={timelineGroups}
        isLoading={isLoading}
        onEventClick={handleEventClick}
        emptyStateTitle="No activities found"
        emptyStateDescription="Try adjusting your date range or removing some filters"
      />

      {/* Assignment Activity Modal */}
      <AssignmentActivityModal
        event={selectedAssignmentEvent}
        open={assignmentModalOpen}
        onClose={() => {
          setAssignmentModalOpen(false)
          setSelectedAssignmentEvent(null)
        }}
      />

      {/* Certificate Activity Modal */}
      <CertificateActivityModal
        event={selectedCEActivity}
        eventType={selectedCEActivityType}
        open={certificateModalOpen}
        onClose={() => {
          setCertificateModalOpen(false)
          setSelectedCEActivity(null)
          setSelectedCEActivityType(null)
        }}
        linkedServiceID={queryParams.linkedServiceID}
      />

      {/* User Action Modal */}
      <UserActionModal
        event={selectedUserAction}
        open={userActionModalOpen}
        onClose={() => {
          setUserActionModalOpen(false)
          setSelectedUserAction(null)
        }}
        linkedServiceID={queryParams.linkedServiceID}
      />

      {/* License Activity Modal */}
      <LicenseActivityModal
        event={selectedLicenseActivity}
        open={licenseModalOpen}
        onClose={() => {
          setLicenseModalOpen(false)
          setSelectedLicenseActivity(null)
        }}
        linkedServiceID={queryParams.linkedServiceID}
      />

      {/* Credential Activity Modal */}
      <CredentialActivityModal
        event={selectedCredentialActivity}
        open={credentialModalOpen}
        onClose={() => {
          setCredentialModalOpen(false)
          setSelectedCredentialActivity(null)
        }}
        linkedServiceID={queryParams.linkedServiceID}
      />
    </Box>
  )
}
